import type { components } from './api.generated';

type Visitor = components['schemas']['Visitor'];
export interface PeerPose extends Visitor {
  readonly walked: number;
  readonly gait: number;
  readonly velocity: readonly [number, number];
}

/** A visitor as the server told of them at its moment `sent`: where they stood at the moment
 *  `at`, which is when their own browser last said so, and the metres walked till then. */
interface Told {
  readonly sent: number;
  readonly at: number;
  /** How far apart that browser's reports are heard to come, in milliseconds, and how many
   *  tellings in a row before this one each brought a later report. */
  readonly reported: number;
  readonly run: number;
  readonly visitor: Visitor;
  readonly walked: number;
}

/**
 * How the other visitors are drawn between what the server tells of them. They are drawn where
 * they were a moment ago, between two tellings, so that a telling which comes late leaves nobody
 * standing and one which comes early sets nobody running to catch up. Every time is the server's.
 */
export const PEERS = {
  /** The tellings are taken to come this far apart until they are heard to, in milliseconds. */
  apartMs: 200,
  /** Drawn this much further back than the latest telling could ever have been needed. */
  spareMs: 20,
  /** How late the last few tellings came is remembered over this many of them. */
  lates: 12,
  /** How far back they are drawn changes by no more than this share of the time passing. */
  easing: 0.1,
  /** With no telling to walk on to, a visitor walks on as it was going for this long. */
  onwardMs: 250,
  /** A move of more than this between two tellings, in metres, is not walked: they are there. */
  leapM: 8,
  /** What is told of a visitor is kept this long, in milliseconds. */
  keptMs: 3000,
  /** A visitor's browser reports where it is this often, give or take, in milliseconds; what
   *  the server tells is its last report, never older than this many of them. */
  reportedMs: 100,
  stale: 1.5,
  /** More reports than this between two tellings is a gap, not a walk to be counted out; how
   *  far apart they come is taken from the count once this many are counted. */
  reports: 6,
  counted: 8,
} as const;

const turned = (from: number, to: number): number =>
  ((to - from + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
const leapt = (from: Visitor, to: Visitor): boolean =>
  from.mode !== to.mode || Math.hypot(to.x - from.x, to.z - from.z) > PEERS.leapM;

export class PeerStore {
  private told = new Map<string, Told[]>();
  private lates: number[] = [];
  private apart: number = PEERS.apartMs;
  private heard: number | null = null;
  /** How far back the visitors are drawn, and when that was last eased. */
  private back: number | null = null;
  private drawn: number | null = null;

  get size(): number {
    return this.told.size;
  }

  /** What the server told at its moment `sent`, heard at its moment `now`. */
  replace(visitors: readonly Visitor[], own: string, sent: number, now: number = sent): void {
    if (this.heard !== null && sent > this.heard)
      this.apart += (Math.min(1000, sent - this.heard) - this.apart) * 0.2;
    this.heard = Math.max(this.heard ?? sent, sent);
    const next = new Map<string, Told[]>();
    let oldest = sent;
    for (const visitor of visitors) {
      if (visitor.id === own) continue;
      const before = (this.told.get(visitor.id) ?? []).filter(
        (one) => one.sent < sent && one.sent > sent - PEERS.keptMs
      );
      const last = before.at(-1);
      const walked =
        last && !leapt(last.visitor, visitor)
          ? last.walked + Math.hypot(visitor.x - last.visitor.x, visitor.z - last.visitor.z)
          : (last?.walked ?? 0);
      const one = this.timed(visitor, sent, before);
      oldest = Math.min(oldest, one.at);
      next.set(visitor.id, [...before, { ...one, sent, visitor, walked }]);
    }
    this.told = next;
    this.lates.push(Math.max(0, now - oldest));
    if (this.lates.length > PEERS.lates) this.lates.shift();
  }

  /* When a visitor stood where the server now tells. The server tells five times a second and a
     browser reports ten times a second, each by its own timer, so one report lies between two
     tellings, or three, where two were meant: drawn by when it was told, a steady walker went
     at half its pace and then at half as much again. Its reports are numbered, and it is drawn
     by their count. No report is told before it is made, so of the tellings of one walk the
     one whose report was freshest fixes when each of the others was made. */
  private timed(
    visitor: Visitor,
    sent: number,
    before: readonly Told[]
  ): Pick<Told, 'at' | 'reported' | 'run'> {
    const last = before.at(-1);
    const reports = last ? visitor.seq - last.visitor.seq : 0;
    if (!last || reports <= 0 || reports > PEERS.reports)
      return { at: sent, reported: last?.reported ?? PEERS.reportedMs, run: 0 };
    const walk = before.slice(-(last.run + 1));
    const first = walk[0] ?? last;
    const counted = visitor.seq - first.visitor.seq;
    const heard = Math.max(80, Math.min(130, (sent - first.sent) / counted));
    const reported =
      counted < PEERS.counted ? last.reported : last.reported + (heard - last.reported) * 0.1;
    const freshest = Math.min(
      sent - visitor.seq * reported,
      ...walk.map((one) => one.sent - one.visitor.seq * reported)
    );
    return {
      at: Math.max(last.at + 1, sent - PEERS.stale * reported, visitor.seq * reported + freshest),
      reported,
      run: last.run + 1,
    };
  }

  /** The visitors as they are drawn at the server's moment `now`. */
  sample(now: number): readonly PeerPose[] {
    const wanted = Math.max(...this.lates, 0) + this.apart + PEERS.spareMs;
    const step = this.drawn === null ? Infinity : Math.max(0, now - this.drawn) * PEERS.easing;
    this.back =
      this.back === null ? wanted : this.back + Math.max(-step, Math.min(step, wanted - this.back));
    this.drawn = now;
    const at = now - this.back;
    return [...this.told.values()].map((told) => this.between(told, at));
  }

  clear(): void {
    this.told.clear();
    this.lates = [];
    this.heard = null;
    this.back = null;
    this.drawn = null;
  }

  private between(told: readonly Told[], at: number): PeerPose {
    const latest = told.at(-1);
    if (!latest) throw new Error('A visitor of whom nothing is told');
    const after = told.findIndex((one) => one.at > at);
    if (after === 0)
      return { ...latest.visitor, ...this.posed(told[0] ?? latest), velocity: [0, 0] };
    const from = (after < 0 ? latest : told[after - 1]) ?? latest;
    const to = after < 0 ? undefined : told[after];
    // What it says and does is the latest told; only where it stands is drawn from behind.
    if (to && !leapt(from.visitor, to.visitor)) {
      const mix = (at - from.at) / (to.at - from.at);
      const velocity = this.pace(from, to);
      return {
        ...latest.visitor,
        x: from.visitor.x + (to.visitor.x - from.visitor.x) * mix,
        y: from.visitor.y + (to.visitor.y - from.visitor.y) * mix,
        z: from.visitor.z + (to.visitor.z - from.visitor.z) * mix,
        yaw: from.visitor.yaw + turned(from.visitor.yaw, to.visitor.yaw) * mix,
        mode: from.visitor.mode,
        walked: from.walked + (to.walked - from.walked) * mix,
        gait: Math.hypot(...velocity) > 0.02 ? 1 : 0,
        velocity,
      };
    }
    if (to) return { ...latest.visitor, ...this.posed(from), velocity: [0, 0] };
    /* Nothing later is told yet. It walks on as it was going for a moment, and then stands. */
    const before = told.at(-2);
    const velocity: readonly [number, number] =
      before && !leapt(before.visitor, from.visitor) ? this.pace(before, from) : [0, 0];
    const onward = Math.min(at - from.at, PEERS.onwardMs) / 1000;
    const walking = at - from.at < PEERS.onwardMs && Math.hypot(...velocity) > 0.02;
    return {
      ...latest.visitor,
      x: from.visitor.x + velocity[0] * onward,
      y: from.visitor.y,
      z: from.visitor.z + velocity[1] * onward,
      yaw: from.visitor.yaw,
      walked: from.walked + Math.hypot(...velocity) * onward,
      gait: walking ? 1 : 0,
      velocity: walking ? velocity : [0, 0],
    };
  }

  private posed(one: Told): Pick<PeerPose, 'x' | 'y' | 'z' | 'yaw' | 'mode' | 'walked' | 'gait'> {
    const { x, y, z, yaw, mode } = one.visitor;
    return { x, y, z, yaw, mode, walked: one.walked, gait: 0 };
  }

  /* Metres a second over the ground between two tellings; a boat is not walked. */
  private pace(from: Told, to: Told): readonly [number, number] {
    if (to.visitor.mode !== 'walk') return [0, 0];
    const seconds = (to.at - from.at) / 1000;
    return [(to.visitor.x - from.visitor.x) / seconds, (to.visitor.z - from.visitor.z) / seconds];
  }
}
